import type {
  Book,
  ConnectorStates,
  PlanningCard,
  PreflightResult,
  PublishingPlatform,
  PublishingProfile,
  RevisionSnapshot,
} from "./models";

function nowIso(): string {
  return new Date().toISOString();
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function totalWords(book: Pick<Book, "chapters">): number {
  const chapters = Array.isArray(book?.chapters) ? book.chapters : [];
  return chapters.reduce((sum: number, chapter) => {
    const words = String(chapter?.content || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
    return sum + words;
  }, 0);
}

export function ensureConnectorStates(value: unknown): ConnectorStates {
  const current = value && typeof value === "object" ? value : {};
  return {
    googleDrive: Boolean((current as Record<string, unknown>).googleDrive),
    oneDrive: Boolean((current as Record<string, unknown>).oneDrive),
    dropbox: Boolean((current as Record<string, unknown>).dropbox),
  };
}

export function createRevisionSnapshot(book: Book, note: string): RevisionSnapshot {
  return {
    id: `snapshot-${Date.now()}`,
    createdAt: nowIso(),
    note: String(note || "").trim() || "Manual snapshot",
    wordCount: totalWords(book),
    book: clone(book),
  };
}

export function restoreRevisionSnapshot(book: Book, snapshot: RevisionSnapshot | null): Book {
  if (!snapshot || !snapshot.book) return clone(book);
  return clone(snapshot.book);
}

export function buildPlanningCards(book: Book): PlanningCard[] {
  const chapters = Array.isArray(book?.chapters) ? book.chapters : [];
  return chapters.map((chapter, index: number) => ({
    id: chapter.id || `chapter-${index + 1}`,
    title: chapter.title || `Chapter ${index + 1}`,
    order: index + 1,
    summary: chapter?.tabData?.summary || "",
      status: chapter?.metadata?.status || "draft",
    tags: Array.isArray(chapter?.metadata?.tags) ? chapter.metadata.tags : [],
  }));
}

export function runPublishingPreflight(
  book: Book,
  options: { platform?: PublishingPlatform; profile?: PublishingProfile }
): PreflightResult {
  const platform: PublishingPlatform = options?.platform || "kdp";
  const profile: PublishingProfile = options?.profile || "print";
  const checks: Array<{ id: string; label: string; pass: boolean; detail: string }> = [];
  const chapters = Array.isArray(book?.chapters) ? book.chapters : [];
  const words = totalWords(book);

  const hasContent = chapters.some((chapter) => String(chapter.content || "").trim().length > 0);
  checks.push({
    id: "content",
    label: "Manuscript has non-empty chapter content",
    pass: hasContent,
    detail: hasContent ? "At least one chapter has content." : "No chapter content found.",
  });

  const hasTitle = Boolean(String(book?.title || "").trim());
  checks.push({
    id: "title",
    label: "Book title is set",
    pass: hasTitle,
    detail: hasTitle ? "Title is present." : "Set a book title before exporting.",
  });

  if (platform === "kdp") {
    checks.push({
      id: "kdp-word-count",
      label: "KDP manuscript word-count baseline",
      pass: words >= 500,
      detail: words >= 500 ? `${words} words detected.` : `Only ${words} words; KDP print workflows are usually longer.`,
    });
    checks.push({
      id: "kdp-chapter-titles",
      label: "KDP chapter headings present",
      pass: chapters.every((chapter) => Boolean(String(chapter.title || "").trim())),
      detail: "Every chapter should have a title for clean TOC generation.",
    });
  } else if (platform === "ingramspark") {
    checks.push({
      id: "ingram-word-count",
      label: "IngramSpark baseline manuscript volume",
      pass: words >= 1000,
      detail: words >= 1000 ? `${words} words detected.` : `Only ${words} words; verify print-readiness.`,
    });
    checks.push({
      id: "ingram-metadata",
      label: "IngramSpark metadata completeness baseline",
      pass: Boolean(book?.metadata?.language),
      detail: "Language metadata should be present.",
    });
  }

  checks.push({
    id: "profile",
    label: "Publishing profile selected",
    pass: profile === "print" || profile === "ebook",
    detail: `Profile: ${profile}`,
  });

  const pass = checks.every((check) => Boolean(check.pass));
  return {
    platform,
    profile,
    pass,
    checks,
    generatedAt: nowIso(),
  };
}
